/**
 * Project Exodus primitive color palette.
 * Foundational brand colors for the PEx design system.
 */
export const exodusColors = {
  neutrals: {
    white: "#FFFFFF",
    lightest: "#EEEEEE",
    lighter: "#CCCCCC",
    light: "#AAAAAA",
    base: "#666666",
    dark: "#444444",
    darker: "#222222",
    darkest: "#000000",
  },
  carrara: {
    lightest: "#FDFDFD",
    lighter: "#FCFCFB",
    light: "#F5F5F3",
    base: "#F2F1EF",
    dark: "#C1C0BF",
    darker: "#60605F",
    darkest: "#484847",
  },
  yellow: {
    lightest: "#FEF9F0",
    lighter: "#FDEED3",
    light: "#FACE85",
    base: "#F59E0B",
    dark: "#BA790B",
    darker: "#744D0A",
    darkest: "#452F0A",
  },
  teal: {
    lightest: "#F0FAFB",
    lighter: "#D0EEF1",
    light: "#7DC7CF",
    base: "#0E8C9C",
    dark: "#0A7786",
    darker: "#085E69",
    darkest: "#054650",
  },
  blue: {
    lightest: "#EEF3FC",
    lighter: "#C8DAEF",
    light: "#6D9BD8",
    base: "#2462BE",
    dark: "#1D52A0",
    darker: "#163E7B",
    darkest: "#0F2D58",
  },
  orange: {
    lightest: "#FDF5EF",
    lighter: "#F9DEC5",
    light: "#E8A368",
    base: "#C96218",
    dark: "#A85013",
    darker: "#7D3C0E",
    darkest: "#572A09",
  },
  green: {
    lightest: "#F8FAF8",
    lighter: "#EAEEEB",
    light: "#C0CDC2",
    base: "#1A7A3C",
    dark: "#156933",
    darker: "#0D4D23",
    darkest: "#083D1B",
  },
  red: {
    lightest: "#F9ECE9",
    lighter: "#F3D9D4",
    light: "#D67A69",
    base: "#C5412A",
    dark: "#9D3421",
    darker: "#4E1A10",
    darkest: "#3B130C",
  },
  peach: {
    lightest: "#FEF9E5",
    lighter: "#FDF4EC",
    light: "#FADABE",
    base: "#F9CBA3",
    dark: "#C7A282",
    darker: "#635141",
    darkest: "#4A3C30",
  },
} as const;

export const baseColors = {
  neutral: {
    50: "#fafafa",
    100: "#f5f5f5",
    200: "#e5e5e5",
    300: "#d4d4d4",
    400: "#a3a3a3",
    500: "#737373",
    600: "#525252",
    700: "#404040",
    800: "#262626",
    900: "#171717",
    950: "#0a0a0a",
  },
  blue: {
    50: "#eff6ff",
    100: "#dbeafe",
    200: "#bfdbfe",
    300: "#93c5fd",
    400: "#60a5fa",
    500: "#3b82f6",
    600: "#2563eb",
    700: "#1d4ed8",
    800: "#1e40af",
    900: "#1e3a8a",
    950: "#172554",
  },
  yellow: {
    50: "#fefce8",
    100: "#fef9c3",
    200: "#fef08a",
    300: "#fde047",
    400: "#facc15",
    500: "#eab308",
    600: "#ca8a04",
    700: "#a16207",
    800: "#854d0e",
    900: "#713f12",
    950: "#422006",
  },
  orange: {
    50: "#fff7ed",
    100: "#ffedd5",
    200: "#fed7aa",
    300: "#fdba74",
    400: "#fb923c",
    500: "#f97316",
    600: "#ea580c",
    700: "#c2410c",
    800: "#9a3412",
    900: "#7c2d12",
    950: "#431407",
  },
  indigo: {
    50: "#eef2ff",
    100: "#e0e7ff",
    200: "#c7d2fe",
    300: "#a5b4fc",
    400: "#818cf8",
    500: "#6366f1",
    600: "#4f46e5",
    700: "#4338ca",
    800: "#3730a3",
    900: "#312e81",
    950: "#1e1b4b",
  },
  red: {
    50: "#fef2f2",
    100: "#fee2e2",
    200: "#fecaca",
    300: "#fca5a5",
    400: "#f87171",
    500: "#ef4444",
    600: "#dc2626",
    700: "#b91c1c",
    800: "#991b1b",
    900: "#7f1d1d",
    950: "#450a0a",
  },
  green: {
    50: "#f0fdf4",
    100: "#dcfce7",
    200: "#bbf7d0",
    300: "#86efac",
    400: "#4ade80",
    500: "#22c55e",
    600: "#16a34a",
    700: "#15803d",
    800: "#166534",
    900: "#14532d",
    950: "#052e16",
  },
  cyan: {
    50: "#ecfeff",
    100: "#cffafe",
    200: "#a5f3fc",
    300: "#67e8f9",
    400: "#22d3ee",
    500: "#06b6d4",
    600: "#0891b2",
    700: "#0e7490",
    800: "#155e75",
    900: "#164e63",
    950: "#083344",
  },
} as const;

/**
 * Per-app brand colors derived from the Exodus primitive palette.
 */
export const brandColors = {
  bridge: exodusColors.yellow.base, // #F59E0B
  circle: exodusColors.teal.base, // #0E8C9C
  habitat: exodusColors.orange.base, // #C96218
  pulse: exodusColors.blue.base, // #2462BE
} as const;

export type ColorScale = keyof typeof baseColors;
export type ColorShade = keyof (typeof baseColors)[ColorScale];
